(function () {
  'use strict';

  /* ==========================================================
   * PART 1 - CART WATCHDOG
   * Enforces the atomiser offer T&Cs in the cart:
   *  - Each "Complimentary Fill" is linked to the 75ml/100ml
   *    fragrance it was added with.
   *  - If that fragrance is removed, only ITS atomiser loses the fill
   *    (the atomiser stays in the bag) and the customer is told why.
   *  - Checkout is held for a moment until the bag has been checked,
   *    so nobody can click through before the clean-up finishes.
   * ========================================================== */

  var FILL_PROPERTY = 'Complimentary Fill';
  var STATUS_PROPERTY = '_Fill Status';
  // Hidden link from the atomiser to the exact bottle it was added with
  // (visible to staff on the order, hidden from customers).
  var LINK_PROPERTY = '_Qualifying Variant';
  var LINK_NAME_PROPERTY = '_Qualifying Fragrance';
  var STATUS_VALUE = 'Not included - no qualifying 75ml/100ml fragrance in order';
  var QUALIFYING_SIZES = ['75ml', '100ml'];
  var QUALIFYING_PRODUCT_TYPE = 'fragrance';
  var NOTICE_KEY = 'atomiserFillNotice';
  // NOTE: the fill is removed by editing the atomiser line in place, so the
  // atomiser itself always stays in the bag.
  var NOTICE_TEXT =
    'Your complimentary atomiser fill has been removed because your bag no longer contains a 75ml or 100ml fragrance. Your travel atomiser is still in your bag.';

  var CART_REQUEST_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CART_CHANGED_EVENT = 'atomiser:cart-changed';

  /* ---------- Shared offer rules (used by the watchdog AND the panel) ---------- */

  function hasFill(item) {
    var props = item.properties || {};
    return props[FILL_PROPERTY] != null && String(props[FILL_PROPERTY]).trim() !== '';
  }

  function isQualifyingFragrance(item) {
    if (hasFill(item)) return false;

    var type = String(item.product_type || '').trim().toLowerCase();
    if (type !== QUALIFYING_PRODUCT_TYPE) return false;

    var values = (item.options_with_values || []).map(function (option) {
      return String(option.value || '').trim().toLowerCase();
    });

    if (!values.length && item.variant_title) {
      item.variant_title.split('/').forEach(function (part) {
        values.push(part.trim().toLowerCase());
      });
    }

    return values.some(function (value) {
      return QUALIFYING_SIZES.indexOf(value) !== -1;
    });
  }

  // Which bottle (fragrance variant ID) an atomiser's fill is linked to.
  function getLinkedVariant(item) {
    var props = item.properties || {};
    var value = props[LINK_PROPERTY];
    if (value == null || String(value).trim() === '') return null;
    var id = Number(value);
    return isNaN(id) ? null : id;
  }

  // Matches every filled atomiser to a qualifying bottle in the bag.
  //  1. Linked atomisers can only use the exact bottle they were added with.
  //  2. Older, unlinked atomisers use any bottle that is still spare.
  // Returns the fills that no longer have a bottle, plus what's left over.
  function allocateFills(items) {
    var pool = {};
    var order = [];
    var fixes = [];
    var qualifying = 0;
    var fills = 0;

    (items || []).forEach(function (item) {
      if (!isQualifyingFragrance(item)) return;
      qualifying += item.quantity;

      if (!pool[item.variant_id]) {
        pool[item.variant_id] = {
          variantId: item.variant_id,
          handle: item.handle,
          name: [item.product_title, item.variant_title].filter(Boolean).join(' '),
          remaining: 0
        };
        order.push(item.variant_id);
      }
      pool[item.variant_id].remaining += item.quantity;
    });

    function take(variantId, quantity) {
      var bottle = pool[variantId];
      if (!bottle) return 0;
      var taken = Math.min(quantity, bottle.remaining);
      bottle.remaining -= taken;
      return taken;
    }

    function addFix(item, keep) {
      if (keep < item.quantity) {
        fixes.push({
          key: item.key,
          variantId: item.variant_id,
          keep: keep,
          remove: item.quantity - keep
        });
      }
    }

    var filled = (items || []).filter(hasFill);

    filled.forEach(function (item) {
      fills += item.quantity;
      var linked = getLinkedVariant(item);
      if (linked === null) return;
      addFix(item, take(linked, item.quantity));
    });

    filled.forEach(function (item) {
      if (getLinkedVariant(item) !== null) return;
      var kept = 0;
      order.forEach(function (variantId) {
        if (kept < item.quantity) kept += take(variantId, item.quantity - kept);
      });
      addFix(item, kept);
    });

    var available = order.reduce(function (total, variantId) {
      return total + pool[variantId].remaining;
    }, 0);

    return {
      fixes: fixes,
      pool: pool,
      order: order,
      qualifying: qualifying,
      fills: fills,
      available: available
    };
  }

  // How many qualifying bottles are in the bag, how many fills already
  // use them, and how many more fills the customer can add.
  function getFillEligibility(items) {
    return allocateFills(items);
  }

  function announceCartChanged() {
    document.dispatchEvent(new CustomEvent(CART_CHANGED_EVENT, { bubbles: true }));
  }

  function initWatchdog() {
    if (window.__atomiserWatchdogLoaded || typeof window.fetch !== 'function') return;
    window.__atomiserWatchdogLoaded = true;

    var nativeFetch = window.fetch.bind(window);
    var pendingRequests = 0;
    var cartVersion = 0; // goes up every time something changes the cart
    var verifiedVersion = -1; // cartVersion at the last completed check
    var syncPromise = null;
    var syncTimer = null;
    var allowCheckout = false;

    /* ---------- Small helpers ---------- */

    function getJSON(path) {
      return nativeFetch(ROOT + path, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(function (res) {
        if (!res.ok) throw new Error('GET ' + path + ' failed: ' + res.status);
        return res.json();
      });
    }

    function postJSON(path, body) {
      return nativeFetch(ROOT + path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        if (!res.ok) {
          return res.text().then(function (text) {
            throw new Error('POST ' + path + ' failed: ' + res.status + ' ' + text);
          });
        }
        return res.json();
      });
    }

    function cartIsVerified() {
      return pendingRequests === 0 && !syncPromise && verifiedVersion === cartVersion;
    }

    /* ---------- Work out which fills are not allowed ---------- */

    function planFixes(items) {
      return allocateFills(items).fixes;
    }

    // Takes the fill OFF the atomiser without removing the atomiser.
    async function stripFill(fix) {
      var noFill = {};
      noFill[FILL_PROPERTY] = '';
      noFill[LINK_PROPERTY] = '';
      noFill[LINK_NAME_PROPERTY] = '';
      noFill[STATUS_PROPERTY] = STATUS_VALUE;

      if (fix.keep === 0) {
        // Whole line: edit the same line in place - quantity unchanged,
        // fill label swapped for an internal "not included" note.
        await postJSON('cart/change.js', {
          id: fix.key,
          quantity: fix.remove,
          properties: noFill
        });
        return;
      }

      // Part of a line (e.g. 2 filled atomisers but only 1 bottle):
      // add the extra atomisers WITHOUT the fill first, and only reduce
      // the original line once that has worked - so nothing is ever lost.
      var extra = {};
      extra[STATUS_PROPERTY] = STATUS_VALUE;

      try {
        await postJSON('cart/add.js', {
          items: [{ id: fix.variantId, quantity: fix.remove, properties: extra }]
        });
      } catch (error) {
        // Couldn't split the line - take the fill off the whole line instead
        // (all atomisers stay in the bag; customer can re-add one fill).
        await postJSON('cart/change.js', {
          id: fix.key,
          quantity: fix.keep + fix.remove,
          properties: noFill
        });
        return;
      }

      await postJSON('cart/change.js', { id: fix.key, quantity: fix.keep });
    }

    // Total quantity of each atomiser variant that carries a fill.
    function countAtomisers(items, variantIds) {
      var totals = {};
      variantIds.forEach(function (id) {
        totals[id] = 0;
      });
      (items || []).forEach(function (item) {
        if (totals.hasOwnProperty(item.variant_id)) totals[item.variant_id] += item.quantity;
      });
      return totals;
    }

    async function enforceOffer() {
      var changed = false;
      var startCart = await getJSON('cart.js');

      if (!planFixes(startCart.items || []).length) return false;

      // Remember how many atomisers are in the bag before we touch anything.
      var atomiserIds = [];
      (startCart.items || []).forEach(function (item) {
        if (hasFill(item) && atomiserIds.indexOf(item.variant_id) === -1) {
          atomiserIds.push(item.variant_id);
        }
      });
      var before = countAtomisers(startCart.items, atomiserIds);

      for (var pass = 0; pass < 2; pass++) {
        var cart = pass === 0 ? startCart : await getJSON('cart.js');
        var fixes = planFixes(cart.items || []);

        if (!fixes.length) break;

        for (var i = 0; i < fixes.length; i++) {
          try {
            await stripFill(fixes[i]);
          } catch (error) {
            console.warn('Atomiser add-on: could not remove fill', fixes[i], error);
          }
        }

        changed = true;
      }

      // Safety net: if any atomiser went missing along the way, put it back
      // (without the fill).
      var endCart = await getJSON('cart.js');
      var after = countAtomisers(endCart.items, atomiserIds);

      for (var j = 0; j < atomiserIds.length; j++) {
        var id = atomiserIds[j];
        var missing = before[id] - after[id];

        if (missing > 0) {
          var restored = {};
          restored[STATUS_PROPERTY] = STATUS_VALUE;
          try {
            await postJSON('cart/add.js', {
              items: [{ id: id, quantity: missing, properties: restored }]
            });
          } catch (error) {
            console.warn('Atomiser add-on: could not restore atomiser', id, error);
          }
        }
      }

      if (changed) {
        announceChange();
        announceCartChanged();
      }
      return changed;
    }

    /* ---------- Tell the customer ---------- */

    function refreshCart() {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );
    }

    function showNotice() {
      if (!document.body) return;

      var el = document.getElementById('atomiser-fill-notice');

      if (!el) {
        el = document.createElement('div');
        el.id = 'atomiser-fill-notice';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        el.style.cssText =
          'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);' +
          'z-index:2147483000;max-width:440px;width:calc(100% - 32px);' +
          'background:#1c1b1b;color:#fff;padding:14px 44px 14px 18px;' +
          'font-size:13px;line-height:1.5;box-shadow:0 4px 18px rgba(0,0,0,.25);';

        var text = document.createElement('span');
        text.textContent = NOTICE_TEXT;

        var close = document.createElement('button');
        close.type = 'button';
        close.setAttribute('aria-label', 'Dismiss');
        close.textContent = '\u00D7';
        close.style.cssText =
          'position:absolute;top:8px;right:12px;background:none;border:0;' +
          'color:#fff;font-size:20px;line-height:1;cursor:pointer;';
        close.addEventListener('click', function () {
          el.remove();
        });

        el.appendChild(text);
        el.appendChild(close);
        document.body.appendChild(el);
      }

      clearTimeout(el.__timer);
      el.__timer = setTimeout(function () {
        el.remove();
      }, 12000);
    }

    function announceChange() {
      // The full cart page doesn't re-render itself, so reload it and
      // show the notice once it's back.
      if (window.theme && window.theme.pageType === 'cart') {
        try {
          sessionStorage.setItem(NOTICE_KEY, '1');
        } catch (error) {}
        window.location.reload();
        return;
      }

      refreshCart();
      showNotice();
    }

    /* ---------- Running the check ---------- */

    function waitForIdle() {
      return new Promise(function (resolve) {
        var waited = 0;
        (function check() {
          if (pendingRequests === 0 || waited >= 5000) return resolve();
          waited += 50;
          setTimeout(check, 50);
        })();
      });
    }

    function runSync() {
      if (syncPromise) return syncPromise;

      syncPromise = waitForIdle()
        .then(function () {
          var startVersion = cartVersion;
          return enforceOffer().then(function (changed) {
            if (cartVersion === startVersion) verifiedVersion = cartVersion;
            return changed;
          });
        })
        .then(
          function (changed) {
            syncPromise = null;
            return changed;
          },
          function (error) {
            syncPromise = null;
            throw error;
          }
        );

      return syncPromise;
    }

    function scheduleSync() {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(function () {
        runSync().catch(function (error) {
          console.warn('Atomiser add-on: could not check cart', error);
        });
      }, 150);
    }

    function onCartRequestStart() {
      pendingRequests++;
      cartVersion++;
    }

    function onCartRequestEnd() {
      pendingRequests = Math.max(0, pendingRequests - 1);
      // Every cart change (including adds) is checked straight away.
      scheduleSync();
      announceCartChanged();
    }

    /* ---------- Watch every cart request (fetch + XHR) ---------- */

    window.fetch = function (input, init) {
      var url =
        input && typeof input === 'object' && 'url' in input ? input.url : String(input || '');

      if (!CART_REQUEST_RE.test(url)) return nativeFetch(input, init);

      onCartRequestStart();

      var promise = nativeFetch(input, init);
      promise.then(onCartRequestEnd, onCartRequestEnd);

      return promise;
    };

    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__atomiserCartUrl = CART_REQUEST_RE.test(String(url)) ? String(url) : null;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (this.__atomiserCartUrl) {
        onCartRequestStart();
        this.addEventListener('loadend', onCartRequestEnd);
      }
      return originalSend.apply(this, arguments);
    };

    /* ---------- Hold checkout until the bag is verified ---------- */

    document.addEventListener(
      'click',
      function (event) {
        if (allowCheckout || cartIsVerified()) return;

        var trigger =
          event.target && event.target.closest
            ? event.target.closest('[name="checkout"], a[href$="/checkout"]')
            : null;

        if (!trigger) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (trigger.__atomiserChecking) return;
        trigger.__atomiserChecking = true;
        trigger.setAttribute('aria-busy', 'true');

        runSync()
          .then(function (changed) {
            return changed || cartIsVerified() ? changed : runSync();
          })
          .then(
            function (changed) {
              return changed;
            },
            function (error) {
              console.warn('Atomiser add-on: check failed, continuing to checkout', error);
              return false;
            }
          )
          .then(function (changed) {
            trigger.__atomiserChecking = false;
            trigger.removeAttribute('aria-busy');

            // If the bag changed, stop so the customer sees it before paying.
            if (changed) return;

            allowCheckout = true;
            trigger.click();
            setTimeout(function () {
              allowCheckout = false;
            }, 2000);
          });
      },
      true
    );

    /* ---------- Check on load / when returning to the tab ---------- */

    function onReady() {
      try {
        if (sessionStorage.getItem(NOTICE_KEY)) {
          sessionStorage.removeItem(NOTICE_KEY);
          showNotice();
        }
      } catch (error) {}

      runSync().catch(function (error) {
        console.warn('Atomiser add-on: could not check cart', error);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', onReady);
    } else {
      onReady();
    }

    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        cartVersion++;
        scheduleSync();
      }
    });

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        cartVersion++; // cart may have changed in another tab
      }
    });
  }

  initWatchdog();

  /* ==========================================================
   * PART 2 - PRODUCT PAGE ADD-ON PANEL
   * - Atomiser + fill options are always visible.
   * - Clicking "Add Atomiser to Bag" checks the bag: if there is no
   *   unused 75ml/100ml fragrance in it, an error is shown instead
   *   (one fill per qualifying bottle).
   * - The atomiser is linked to the fragrance on this page (or another
   *   unused qualifying bottle in the bag).
   * - If that fragrance is removed later, Part 1 removes the fill.
   * ========================================================== */

  let currentInstance = null;

  function tryInit() {
    const addon = document.getElementById('atomiser-add-on');
    if (!addon) return;

    if (currentInstance && currentInstance.el === addon) return;

    const handle = addon.dataset.productHandle;
    const data = window.atomiserAddOnData && window.atomiserAddOnData[handle];

    if (!data) {
      console.warn('Atomiser add-on: data not found');
      return;
    }

    currentInstance = new AtomiserAddOn(addon, data);
  }

  function startObserver() {
    if (!document.body) return;

    const observer = new MutationObserver(function () {
      const addon = document.getElementById('atomiser-add-on');

      if (addon && (!currentInstance || currentInstance.el !== addon)) {
        tryInit();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  class AtomiserAddOn {
    constructor(el, data) {
      this.el = el;
      this.data = data;

      this.state = {
        fillsAvailable: 0, // qualifying bottles in the bag not yet used by a fill
        cartChecked: false,
        selectedAtomiser: null,
        selectedFill: null
      };

      this.els = {
        toggle: document.getElementById('atomiser-add-on-toggle'),
        panel: document.getElementById('atomiser-add-on-panel'),
        qualifierNotice: document.getElementById('atomiser-qualifier-notice'),
        qualifierText: document.getElementById('atomiser-qualifier-text'),
        atomiserSelect: document.getElementById('addon-atomiser-select'),
        atomiserPriceNote: document.getElementById('addon-atomiser-price-note'),
        atomiserField: document.getElementById('addon-atomiser-field'),
        fillField: document.getElementById('addon-fill-field'),
        fillSelect: document.getElementById('addon-fill-select'),
        actions: document.getElementById('addon-actions'),
        addBtn: document.getElementById('addon-add-btn'),
        addedConfirm: document.getElementById('addon-added-confirm'),
        addedText: document.getElementById('addon-added-text'),
        changeBtn: document.getElementById('addon-change-btn')
      };

      if (!this.els.toggle || !this.els.panel) {
        console.warn('Atomiser add-on: required elements missing');
        return;
      }

      // Options are always visible; the bag is checked when "Add" is clicked.
      if (this.els.qualifierNotice) this.els.qualifierNotice.style.display = 'none';
      if (this.els.atomiserField) this.els.atomiserField.style.display = 'block';
      this.createErrorMessage();

      this.bindEvents();
      this.watchCart();
      this.loadAtomisers();
      this.loadFillFragrances();

      console.log('Atomiser add-on initialized for', data.productTitle);
    }

    bindEvents() {
      this.els.toggle.addEventListener('click', () => this.togglePanel());

      if (this.els.atomiserSelect) {
        this.els.atomiserSelect.addEventListener('change', () => this.onAtomiserChange());
      }

      if (this.els.fillSelect) {
        this.els.fillSelect.addEventListener('change', () => this.onFillChange());
      }

      if (this.els.addBtn) {
        this.els.addBtn.addEventListener('click', () => this.addAtomiserToCart());
      }

      if (this.els.changeBtn) {
        this.els.changeBtn.addEventListener('click', () => this.resetAddOn());
      }
    }

    togglePanel() {
      const isOpen = !this.els.panel.hidden;

      this.els.panel.hidden = isOpen;
      this.els.toggle.setAttribute('aria-expanded', String(!isOpen));

      const icon = this.els.toggle.querySelector('.atomiser-add-on__toggle-icon');
      if (icon) icon.textContent = isOpen ? '+' : '-';
    }

    // Keeps track of what is actually IN THE BAG.
    watchCart() {
      let timer = null;

      const recheck = () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.checkCart(), 200);
      };

      document.addEventListener(CART_CHANGED_EVENT, recheck);
      window.addEventListener('pageshow', recheck);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') recheck();
      });

      this.checkCart();
    }

    async checkCart() {
      try {
        const res = await fetch(ROOT + 'cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });

        if (!res.ok) throw new Error('Cart fetch failed');

        const cart = await res.json();
        const eligibility = getFillEligibility(cart.items);

        this.state.fillsAvailable = eligibility.available;
        this.state.cartChecked = true;

        // If the fill this panel added has since been removed (e.g. the
        // fragrance was taken out), clear the "added" confirmation.
        if (eligibility.fills === 0 && this.isShowingConfirm()) {
          this.resetAddOn();
          return eligibility;
        }

        this.updateEligibilityUI();
        return eligibility;
      } catch (error) {
        console.warn('Atomiser add-on: could not check bag', error);
        this.state.fillsAvailable = 0;
        this.updateEligibilityUI();
        return null;
      }
    }

    isShowingConfirm() {
      return !!(this.els.addedConfirm && this.els.addedConfirm.style.display === 'flex');
    }

    // Red message shown under the "Add Atomiser to Bag" button.
    createErrorMessage() {
      const error = document.createElement('p');
      error.id = 'addon-error';
      error.className = 'atomiser-add-on__error';
      error.setAttribute('role', 'alert');
      error.style.cssText =
        'display:none;margin:12px 0 0;padding:10px 12px;border:1px solid #e3b4b0;' +
        'background:#fdf3f2;color:#b3261e;font-size:13px;line-height:1.5;text-align:left;';

      const anchor = this.els.actions || this.els.fillField || this.els.atomiserField;
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(error, anchor.nextSibling);
      } else {
        this.els.panel.appendChild(error);
      }

      this.els.error = error;
    }

    showError(message) {
      if (!this.els.error) return;
      this.els.error.textContent = message;
      this.els.error.style.display = 'block';
    }

    clearError() {
      if (!this.els.error) return;
      this.els.error.textContent = '';
      this.els.error.style.display = 'none';
    }

    // Called whenever the bag changes. The options stay visible; any old
    // error is cleared (the bag is checked again on the next click).
    updateEligibilityUI() {
      this.clearError();
      this.validateAddBtn();
    }

    async loadAtomisers() {
      if (!this.els.atomiserSelect) return;

      try {
        const res = await fetch(
          `/collections/${this.data.atomiserCollectionHandle}/products.json?limit=50`
        );

        if (!res.ok) throw new Error('Atomiser collection not found');

        const result = await res.json();
        this.populateAtomiserSelect(result.products || []);
      } catch (error) {
        console.warn('Atomiser add-on: could not load atomisers', error);
        this.els.atomiserSelect.innerHTML =
          '<option value="">Atomisers unavailable</option>';
      }
    }

    populateAtomiserSelect(products) {
      this.els.atomiserSelect.innerHTML =
        '<option value="">Select an Atomiser</option>';

      products.forEach(product => {
        (product.variants || []).forEach(variant => {
          const option = document.createElement('option');

          option.value = JSON.stringify({
            productTitle: product.title,
            variantTitle: variant.title,
            variantId: variant.id,
            price: variant.price
          });

          const price = parseFloat(variant.price).toFixed(2);
          option.textContent =
            product.variants.length > 1
              ? `${product.title} - ${variant.title} - $${price}`
              : `${product.title} - $${price}`;

          this.els.atomiserSelect.appendChild(option);
        });
      });
    }

    async loadFillFragrances() {
      if (!this.els.fillSelect) return;

      try {
        const res = await fetch(
          `/collections/${this.data.fragranceCollectionHandle}/products.json?limit=250`
        );

        if (!res.ok) throw new Error('Fragrance collection not found');

        const result = await res.json();
        this.populateFillSelect(result.products || []);
      } catch (error) {
        console.warn('Atomiser add-on: could not load fragrances', error);
        this.els.fillSelect.innerHTML =
          '<option value="">Fragrances unavailable</option>';
      }
    }

    populateFillSelect(products) {
      this.els.fillSelect.innerHTML =
        '<option value="">Select a Fragrance</option>';

      products.forEach(product => {
        const option = document.createElement('option');
        option.value = product.title;
        option.textContent = product.title;

        if (product.title === this.data.productTitle) {
          option.selected = true;
          this.state.selectedFill = product.title;
        }

        this.els.fillSelect.appendChild(option);
      });

      this.validateAddBtn();
    }

    onAtomiserChange() {
      const value = this.els.atomiserSelect.value;

      if (!value) {
        this.state.selectedAtomiser = null;

        if (this.els.atomiserPriceNote) this.els.atomiserPriceNote.textContent = '';
        if (this.els.fillField) this.els.fillField.style.display = 'none';
        if (this.els.actions) this.els.actions.style.display = 'none';

        this.validateAddBtn();
        return;
      }

      this.state.selectedAtomiser = JSON.parse(value);
      this.clearError();

      const price = parseFloat(this.state.selectedAtomiser.price).toFixed(2);

      if (this.els.atomiserPriceNote) {
        this.els.atomiserPriceNote.textContent = `$${price} - added to your bag`;
      }

      if (this.els.fillField) this.els.fillField.style.display = 'block';
      if (this.els.actions) this.els.actions.style.display = 'flex';

      this.validateAddBtn();
    }

    onFillChange() {
      this.state.selectedFill = this.els.fillSelect.value || null;
      this.validateAddBtn();
    }

    validateAddBtn() {
      if (!this.els.addBtn) return;

      // The bag itself is checked when the button is clicked.
      const valid =
        this.state.selectedAtomiser &&
        this.state.selectedFill;

      this.els.addBtn.disabled = !valid;
    }

    async addAtomiserToCart() {
      if (!this.state.selectedAtomiser || !this.state.selectedFill) return;

      this.clearError();
      this.els.addBtn.disabled = true;
      this.els.addBtn.textContent = 'Checking your bag...';

      // Check the bag at the moment of adding.
      const eligibility = await this.checkCart();
      this.clearError();

      if (!eligibility || eligibility.available < 1) {
        this.els.addBtn.textContent = 'Add Atomiser to Bag';
        this.validateAddBtn();

        if (!eligibility) {
          this.showError('We couldn\'t check your bag. Please refresh the page and try again.');
        } else if (eligibility.qualifying === 0) {
          this.showError(
            'Please add a 75ml or 100ml fragrance to your bag first. The complimentary fill is only available with the purchase of a 75ml or 100ml fragrance.'
          );
        } else {
          this.showError(
            'Each 75ml or 100ml fragrance includes one complimentary fill. Please add another 75ml or 100ml fragrance to your bag to fill an additional atomiser.'
          );
        }
        return;
      }

      this.els.addBtn.textContent = 'Adding...';

      // Link the atomiser to a specific bottle with a spare fill:
      // the fragrance on this page if it's in the bag, otherwise another
      // 75ml/100ml bottle in the bag that hasn't been used yet.
      const spareBottles = eligibility.order
        .map(variantId => eligibility.pool[variantId])
        .filter(bottle => bottle.remaining > 0);

      const linkedBottle =
        spareBottles.find(bottle => bottle.handle === this.el.dataset.productHandle) ||
        spareBottles[0];

      const properties = {};
      properties[FILL_PROPERTY] = this.state.selectedFill;
      properties[LINK_PROPERTY] = String(linkedBottle.variantId);
      properties[LINK_NAME_PROPERTY] = linkedBottle.name;

      try {
        const res = await fetch(ROOT + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: this.state.selectedAtomiser.variantId,
                quantity: 1,
                properties: properties
              }
            ]
          })
        });

        if (!res.ok) throw new Error('Cart add failed');

        if (this.els.actions) this.els.actions.style.display = 'none';

        if (this.els.addedText) {
          this.els.addedText.textContent =
            `${this.state.selectedAtomiser.productTitle} added and filled with ${this.state.selectedFill}`;
        }

        if (this.els.addedConfirm) {
          this.els.addedConfirm.style.display = 'flex';
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:open', { bubbles: true }));
      } catch (error) {
        console.error('Atomiser add-on: cart error', error);

        this.els.addBtn.disabled = false;
        this.els.addBtn.textContent = 'Try Again';
      }
    }

    resetAddOn() {
      this.state.selectedAtomiser = null;

      if (this.els.atomiserSelect) this.els.atomiserSelect.value = '';
      if (this.els.atomiserPriceNote) this.els.atomiserPriceNote.textContent = '';
      if (this.els.fillField) this.els.fillField.style.display = 'none';
      if (this.els.actions) this.els.actions.style.display = 'none';
      if (this.els.addedConfirm) this.els.addedConfirm.style.display = 'none';
      if (this.els.addBtn) this.els.addBtn.textContent = 'Add Atomiser to Bag';

      this.updateEligibilityUI();
    }
  }

  document.addEventListener('atomiser-add-on:data-ready', tryInit);
  document.addEventListener('DOMContentLoaded', function () {
    tryInit();
    startObserver();
  });

  if (document.readyState !== 'loading') {
    tryInit();
    startObserver();
  }

  setTimeout(tryInit, 800);
  setTimeout(tryInit, 2000);
})();